import { Copy, Download, FilterX, RefreshCw, Settings2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { copyToClipboard, exportToCSV, type ColumnDef } from './tenderTableUtils';

interface ToolbarProps<T> {
  items: T[];
  columns: ColumnDef<T>[];
  csvFilename: string;
  hasActiveColumnFilters: boolean;
  onClearColumnFilters: () => void;
  isVisible: (key: string) => boolean;
  toggleColumn: (key: string) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  countLabel: string;
  extra?: React.ReactNode;
}

export function TenderTableToolbar<T>({
  items,
  columns,
  csvFilename,
  hasActiveColumnFilters,
  onClearColumnFilters,
  isVisible,
  toggleColumn,
  onRefresh,
  isRefreshing,
  countLabel,
  extra,
}: ToolbarProps<T>) {
  return (
    <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-muted/10">
      <span className="text-xs text-muted-foreground">{countLabel}</span>
      <div className="flex items-center gap-2">
        {extra}
        {hasActiveColumnFilters && (
          <Button variant="outline" size="sm" onClick={onClearColumnFilters}>
            <FilterX className="h-4 w-4 mr-1" /> Clear filters
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            try {
              await copyToClipboard(items, columns);
              toast.success('Copied to clipboard');
            } catch {
              toast.error('Failed to copy');
            }
          }}
        >
          <Copy className="h-4 w-4 mr-1" /> Copy
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            exportToCSV(items, columns, csvFilename);
            toast.success(`Exported ${items.length} rows`);
          }}
        >
          <Download className="h-4 w-4 mr-1" /> CSV
        </Button>
        {onRefresh && (
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing}>
            <RefreshCw className={cn('h-4 w-4 mr-1', isRefreshing && 'animate-spin')} />
            Refresh
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Settings2 className="h-4 w-4 mr-1" /> Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {columns.filter((c) => !c.noFilter).map((col) => (
              <DropdownMenuCheckboxItem
                key={col.key}
                checked={isVisible(col.key)}
                onCheckedChange={() => toggleColumn(col.key)}
              >
                {col.label || col.key}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
